/**
 * Service Desk Workload — analyst productivity, capacity metrics, and staffing projections.
 *
 * Shows per-analyst ticket counts, resolution times, call volumes,
 * workload trends, and capacity thresholds.
 */

import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { apiClient } from '@/services/apiClient';
import { LoadingSpinner } from '@/components/ui/LoadingSpinner';
import { ErrorMessage } from '@/components/ui/ErrorMessage';

interface AnalystData {
  ResponsibleFullName: string;
  totalTickets: number;
  closedTickets: number;
  avgResolutionHours: number | null;
  newHireTickets: number;
  terminationTickets: number;
  openTickets: number;
}

interface WorkloadData {
  period: { days: number; workingDays: number };
  team: { size: number; analysts: AnalystData[] };
  capacity: {
    ticketsPerPersonPerDay: number;
    callsPerPersonPerDay: number;
    workItemsPerPersonPerDay: number;
    totalWorkItems: number;
    totalTickets: number;
    totalCalls: number;
    answeredCalls: number;
    abandonedCalls: number;
    avgCallDuration: number;
    avgResolutionHours: number | null;
  };
  trend: {
    growthPercent: number;
    recentPeriodTickets: number;
    earlierPeriodTickets: number;
    avgTicketsPerWeek: number;
    avgTicketsPerDay: number;
  };
  categoryBreakdown: Array<{ category: string; cnt: number }>;
  weeklyVelocity: Array<{ weekStart: string; opened: number; closed: number }>;
  dayOfWeekVolume: Array<{ dayName: string; dayNum: number; avgTickets: number }>;
  hourlyVolume: Array<{ hour: number; avgTickets: number }>;
  resolutionDistribution: Array<{ bucket: string; cnt: number }>;
  backlogTrend: Array<{ weekStart: string; openAtEnd: number }>;
  avgFirstResponseMinutes: number | null;
  sameDayRate: Array<{ ResponsibleFullName: string; totalClosed: number; sameDayClosed: number; rate: number }>;
  agingTickets: Array<{ ResponsibleFullName: string; ticketId: number; title: string; daysOpen: number; statusName: string }>;
  categoryPerAnalyst: Array<{ ResponsibleFullName: string; category: string; cnt: number }>;
  repeatRequestors: Array<{ RequestorName: string; RequestorEmail: string; ticketCount: number }>;
  locationHotspots: Array<{ siteNumber: string; branchName: string; ticketCount: number; employeeCount: number; ticketsPerEmployee: number }>;
  callTicketCorrelation: { totalTickets: number; correlatedWithCall: number; correlationPercent: number } | null;
  deploymentLoad: { computerReplacements: number; computerShipping: number; ofReplacements: number; ofRMAs: number; ofShipping: number; termRecoveries: number };
  dailyVolume: Array<{ day: string; tickets: number; closed: number }>;
  generatedAt: string;
}

const PERIOD_OPTIONS = [
  { value: 30, label: '30 Days' },
  { value: 60, label: '60 Days' },
  { value: 90, label: '90 Days' },
  { value: 180, label: '6 Months' },
  { value: 365, label: '1 Year' },
];

export function ServiceDeskWorkload() {
  const [days, setDays] = useState(30);
  const [capacityThreshold, setCapacityThreshold] = useState(30);

  const { data, isLoading, error } = useQuery({
    queryKey: ['service-desk-workload', days],
    queryFn: () => apiClient.get<WorkloadData>(`service-desk/workload?days=${days}`),
  });

  if (isLoading) return <LoadingSpinner message="Loading workload data…" />;
  if (error) return <ErrorMessage message={(error as Error).message} />;
  if (!data) return null;

  const atCapacity = data.capacity.workItemsPerPersonPerDay >= capacityThreshold;

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-lg font-semibold text-text-primary">Service Desk Workload</h1>
          <p className="text-sm text-slate-500 mt-1">Team of {data.team.size} analysts — {data.period.workingDays} working days</p>
        </div>
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2">
            <label className="text-xs text-slate-500">Capacity threshold:</label>
            <input
              type="number"
              value={capacityThreshold}
              onChange={(e) => setCapacityThreshold(Number(e.target.value) || 20)}
              min={1}
              max={100}
              className="py-1.5 px-2 rounded-lg border border-border text-sm w-16 text-center font-medium"
            />
            <span className="text-xs text-slate-400">/person/day</span>
          </div>
          <div className="flex gap-2">
            {PERIOD_OPTIONS.map(opt => (
              <button
                key={opt.value}
                onClick={() => setDays(opt.value)}
                className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${
                  days === opt.value
                    ? 'bg-baker-red text-white'
                    : 'bg-white border border-border text-slate-600 hover:bg-page'
                }`}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Capacity KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-4 mb-6">
        <KPI label="Work Items/Person/Day" value={data.capacity.workItemsPerPersonPerDay.toFixed(1)} alert={atCapacity} sub="tickets + calls" />
        <KPI label="Tickets/Person/Day" value={data.capacity.ticketsPerPersonPerDay.toFixed(1)} />
        <KPI label="Calls/Person/Day" value={data.capacity.callsPerPersonPerDay.toFixed(1)} />
        <KPI label="Total Work Items" value={data.capacity.totalWorkItems.toLocaleString()} sub={`${data.capacity.totalTickets} tickets + ${data.capacity.totalCalls} calls`} />
        <KPI label="Total Calls" value={data.capacity.totalCalls.toLocaleString()} sub={`${data.capacity.answeredCalls} answered`} />
        <KPI label="Avg Resolution" value={data.capacity.avgResolutionHours != null ? `${data.capacity.avgResolutionHours}h` : '—'} />
        <KPI label="Volume Trend" value={`${data.trend.growthPercent > 0 ? '+' : ''}${data.trend.growthPercent}%`} alert={data.trend.growthPercent > 20} sub="vs prior period" />
      </div>

      {/* Capacity Warning */}
      {atCapacity && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6 text-red-700 text-sm">
          ⚠️ <strong>At capacity.</strong> Your team is handling {data.capacity.workItemsPerPersonPerDay} work items/person/day (threshold: {capacityThreshold}). Consider adding headcount or reducing inflow.
        </div>
      )}

      {data.trend.growthPercent > 15 && !atCapacity && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6 text-amber-700 text-sm">
          📈 <strong>Volume growing.</strong> Ticket volume is up {data.trend.growthPercent}% compared to the prior period ({data.trend.earlierPeriodTickets} → {data.trend.recentPeriodTickets}). At this rate, you may need additional headcount within 2-3 months.
        </div>
      )}

      {/* Hardware Deployment Load */}
      {data.deploymentLoad && (
        <div className="bg-white rounded-lg border border-border p-5 mb-6">
          <h2 className="text-base font-semibold text-text-primary mb-4">Hardware Deployment Load</h2>

          {/* Computers */}
          <div className="mb-4">
            <h3 className="text-sm font-medium text-slate-600 mb-2">Computers (tied to people — new hires, refreshes, replacements)</h3>
            <div className="grid grid-cols-3 gap-4">
              <div className="text-center p-3 bg-amber-50 rounded-lg">
                <div className="text-xl font-bold text-amber-700">{data.deploymentLoad.computerReplacements}</div>
                <div className="text-xs text-slate-500">Replacements</div>
              </div>
              <div className="text-center p-3 bg-purple-50 rounded-lg">
                <div className="text-xl font-bold text-purple-700">{data.deploymentLoad.computerShipping}</div>
                <div className="text-xs text-slate-500">Currently Shipping</div>
              </div>
              <div className="text-center p-3 bg-slate-50 rounded-lg">
                <div className="text-xl font-bold text-slate-700">{data.deploymentLoad.termRecoveries}</div>
                <div className="text-xs text-slate-500">Term Recoveries</div>
              </div>
            </div>
          </div>

          {/* OF Devices */}
          <div>
            <h3 className="text-sm font-medium text-slate-600 mb-2">OF Devices / Sleds (shared devices — break/fix)</h3>
            <div className="grid grid-cols-3 gap-4">
              <div className="text-center p-3 bg-orange-50 rounded-lg">
                <div className="text-xl font-bold text-orange-700">{data.deploymentLoad.ofReplacements}</div>
                <div className="text-xs text-slate-500">Replacements</div>
              </div>
              <div className="text-center p-3 bg-red-50 rounded-lg">
                <div className="text-xl font-bold text-red-700">{data.deploymentLoad.ofRMAs}</div>
                <div className="text-xs text-slate-500">RMAs</div>
              </div>
              <div className="text-center p-3 bg-purple-50 rounded-lg">
                <div className="text-xl font-bold text-purple-700">{data.deploymentLoad.ofShipping}</div>
                <div className="text-xs text-slate-500">Currently Shipping</div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Per-Analyst Breakdown */}
      <div className="bg-white rounded-lg border border-border p-5 mb-6">
        <h2 className="text-base font-semibold text-text-primary mb-3">Analyst Workload</h2>
        {data.team.analysts.length === 0 ? (
          <p className="text-slate-500 text-sm">No analyst assignment data available yet. The TDX ticket sync needs to run to populate ResponsibleFullName.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full border-collapse text-[13px]">
              <thead>
                <tr className="border-b-2 border-border">
                  <th className="text-left py-2 px-2 font-medium text-slate-600">Analyst</th>
                  <th className="text-right py-2 px-2 font-medium text-slate-600">Total</th>
                  <th className="text-right py-2 px-2 font-medium text-slate-600">Closed</th>
                  <th className="text-right py-2 px-2 font-medium text-slate-600">Open</th>
                  <th className="text-right py-2 px-2 font-medium text-slate-600">New Hires</th>
                  <th className="text-right py-2 px-2 font-medium text-slate-600">Terms</th>
                  <th className="text-right py-2 px-2 font-medium text-slate-600">Avg Resolution</th>
                  <th className="text-right py-2 px-2 font-medium text-slate-600">Per Day</th>
                </tr>
              </thead>
              <tbody>
                {data.team.analysts.map((a) => {
                  const perDay = data.period.workingDays > 0 ? (a.totalTickets / data.period.workingDays).toFixed(1) : '—';
                  return (
                    <tr key={a.ResponsibleFullName} className="border-b border-border-subtle hover:bg-page/50">
                      <td className="py-2 px-2 font-medium">{a.ResponsibleFullName}</td>
                      <td className="py-2 px-2 text-right">{a.totalTickets}</td>
                      <td className="py-2 px-2 text-right text-green-600">{a.closedTickets}</td>
                      <td className="py-2 px-2 text-right text-amber-600">{a.openTickets}</td>
                      <td className="py-2 px-2 text-right">{a.newHireTickets}</td>
                      <td className="py-2 px-2 text-right">{a.terminationTickets}</td>
                      <td className="py-2 px-2 text-right">{a.avgResolutionHours != null ? `${a.avgResolutionHours.toFixed(1)}h` : '—'}</td>
                      <td className="py-2 px-2 text-right font-medium">{perDay}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Category Breakdown + Trend */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
        {/* Category Breakdown */}
        <div className="bg-white rounded-lg border border-border p-5">
          <h2 className="text-base font-semibold text-text-primary mb-3">Ticket Categories</h2>
          <div className="space-y-3">
            {data.categoryBreakdown.map((cat) => {
              const pct = data.capacity.totalTickets > 0 ? (cat.cnt / data.capacity.totalTickets) * 100 : 0;
              return (
                <div key={cat.category}>
                  <div className="flex justify-between text-sm mb-1">
                    <span className="text-slate-700">{cat.category}</span>
                    <span className="text-slate-500">{cat.cnt} ({pct.toFixed(0)}%)</span>
                  </div>
                  <div className="w-full bg-slate-100 rounded-full h-2">
                    <div className="h-2 rounded-full bg-baker-red" style={{ width: `${pct}%` }} />
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Weekly Trend */}
        <div className="bg-white rounded-lg border border-border p-5">
          <h2 className="text-base font-semibold text-text-primary mb-3">Capacity Metrics</h2>
          <div className="space-y-4">
            <div className="flex justify-between items-center">
              <span className="text-sm text-slate-600">Work items/person/day</span>
              <span className={`text-lg font-bold ${atCapacity ? 'text-red-600' : ''}`}>{data.capacity.workItemsPerPersonPerDay.toFixed(1)}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm text-slate-600">Tickets/person/day</span>
              <span className="text-lg font-bold">{data.capacity.ticketsPerPersonPerDay.toFixed(1)}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm text-slate-600">Calls/person/day</span>
              <span className="text-lg font-bold">{data.capacity.callsPerPersonPerDay.toFixed(1)}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm text-slate-600">Capacity threshold</span>
              <span className="text-lg font-bold text-slate-400">{capacityThreshold}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm text-slate-600">Headroom</span>
              <span className={`text-lg font-bold ${atCapacity ? 'text-red-600' : 'text-green-600'}`}>
                {atCapacity ? 'OVER' : `${(capacityThreshold - data.capacity.workItemsPerPersonPerDay).toFixed(1)} items/day`}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Daily Volume Chart (table form) */}
      {data.dailyVolume.length > 0 && (
        <div className="bg-white rounded-lg border border-border p-5">
          <h2 className="text-base font-semibold text-text-primary mb-3">Daily Volume (Last {Math.min(days, 30)} Days)</h2>
          <div className="overflow-y-auto max-h-[300px]">
            <table className="w-full border-collapse text-[13px]">
              <thead className="sticky top-0 bg-white">
                <tr className="border-b-2 border-border">
                  <th className="text-left py-2 px-2 font-medium text-slate-600">Date</th>
                  <th className="text-right py-2 px-2 font-medium text-slate-600">Tickets</th>
                  <th className="text-right py-2 px-2 font-medium text-slate-600">Closed</th>
                  <th className="text-left py-2 px-2 font-medium text-slate-600">Volume</th>
                </tr>
              </thead>
              <tbody>
                {data.dailyVolume.slice(0, 30).map((d) => {
                  const maxTickets = Math.max(...data.dailyVolume.slice(0, 30).map(x => x.tickets), 1);
                  const pct = (d.tickets / maxTickets) * 100;
                  return (
                    <tr key={d.day} className="border-b border-border-subtle">
                      <td className="py-1.5 px-2">{new Date(d.day + 'T12:00:00').toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}</td>
                      <td className="py-1.5 px-2 text-right font-medium">{d.tickets}</td>
                      <td className="py-1.5 px-2 text-right text-green-600">{d.closed}</td>
                      <td className="py-1.5 px-2">
                        <div className="w-full bg-slate-100 rounded-full h-2">
                          <div className="h-2 rounded-full bg-baker-red/70" style={{ width: `${pct}%` }} />
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Weekly Velocity — opened vs closed */}
      {data.weeklyVelocity && data.weeklyVelocity.length > 0 && (
        <div className="bg-white rounded-lg border border-border p-5 mb-6">
          <h2 className="text-base font-semibold text-text-primary mb-3">Weekly Velocity — Opened vs Resolved</h2>
          <p className="text-xs text-slate-500 mb-3">Are you keeping up? Green = closing more than opening. Red = falling behind.</p>
          <div className="overflow-y-auto max-h-[300px]">
            <table className="w-full border-collapse text-[13px]">
              <thead className="sticky top-0 bg-white">
                <tr className="border-b-2 border-border">
                  <th className="text-left py-2 px-2 font-medium text-slate-600">Week</th>
                  <th className="text-right py-2 px-2 font-medium text-slate-600">Opened</th>
                  <th className="text-right py-2 px-2 font-medium text-slate-600">Resolved</th>
                  <th className="text-right py-2 px-2 font-medium text-slate-600">Net</th>
                </tr>
              </thead>
              <tbody>
                {data.weeklyVelocity.map((w) => {
                  const net = w.closed - w.opened;
                  return (
                    <tr key={w.weekStart} className="border-b border-border-subtle">
                      <td className="py-1.5 px-2">{new Date(w.weekStart.length === 10 ? w.weekStart + 'T12:00:00' : w.weekStart).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</td>
                      <td className="py-1.5 px-2 text-right">{w.opened}</td>
                      <td className="py-1.5 px-2 text-right text-green-600">{w.closed}</td>
                      <td className={`py-1.5 px-2 text-right font-medium ${net >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                        {net >= 0 ? '+' : ''}{net}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Resolution Time + Day/Hour patterns */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        {/* Resolution Distribution */}
        {data.resolutionDistribution && data.resolutionDistribution.length > 0 && (
          <div className="bg-white rounded-lg border border-border p-5">
            <h2 className="text-sm font-semibold text-text-primary mb-3">Resolution Time</h2>
            <div className="space-y-2">
              {data.resolutionDistribution.map((r) => {
                const maxCnt = Math.max(...data.resolutionDistribution.map(x => x.cnt), 1);
                return (
                  <div key={r.bucket}>
                    <div className="flex justify-between text-xs mb-0.5">
                      <span className="text-slate-600">{r.bucket}</span>
                      <span className="text-slate-500">{r.cnt}</span>
                    </div>
                    <div className="w-full bg-slate-100 rounded-full h-1.5">
                      <div className="h-1.5 rounded-full bg-blue-500" style={{ width: `${(r.cnt / maxCnt) * 100}%` }} />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Busiest Day of Week */}
        {data.dayOfWeekVolume && data.dayOfWeekVolume.length > 0 && (
          <div className="bg-white rounded-lg border border-border p-5">
            <h2 className="text-sm font-semibold text-text-primary mb-3">Busiest Days</h2>
            <div className="space-y-2">
              {data.dayOfWeekVolume.filter(d => d.dayNum >= 2 && d.dayNum <= 6).map((d) => {
                const maxAvg = Math.max(...data.dayOfWeekVolume.map(x => x.avgTickets), 1);
                return (
                  <div key={d.dayName}>
                    <div className="flex justify-between text-xs mb-0.5">
                      <span className="text-slate-600">{d.dayName}</span>
                      <span className="text-slate-500">{Math.round(d.avgTickets)} avg</span>
                    </div>
                    <div className="w-full bg-slate-100 rounded-full h-1.5">
                      <div className="h-1.5 rounded-full bg-amber-500" style={{ width: `${(d.avgTickets / maxAvg) * 100}%` }} />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Busiest Hours */}
        {data.hourlyVolume && data.hourlyVolume.length > 0 && (
          <div className="bg-white rounded-lg border border-border p-5">
            <h2 className="text-sm font-semibold text-text-primary mb-3">Busiest Hours</h2>
            <div className="space-y-1">
              {data.hourlyVolume.filter(h => h.hour >= 7 && h.hour <= 18).map((h) => {
                const maxAvg = Math.max(...data.hourlyVolume.map(x => x.avgTickets), 1);
                const label = h.hour === 0 ? '12am' : h.hour < 12 ? `${h.hour}am` : h.hour === 12 ? '12pm' : `${h.hour - 12}pm`;
                return (
                  <div key={h.hour} className="flex items-center gap-2">
                    <span className="text-xs text-slate-500 w-10">{label}</span>
                    <div className="flex-1 bg-slate-100 rounded-full h-1.5">
                      <div className="h-1.5 rounded-full bg-emerald-500" style={{ width: `${(h.avgTickets / maxAvg) * 100}%` }} />
                    </div>
                    <span className="text-xs text-slate-400 w-6 text-right">{Math.round(h.avgTickets)}</span>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {/* Backlog Trend */}
      {data.backlogTrend && data.backlogTrend.length > 0 && (
        <div className="bg-white rounded-lg border border-border p-5 mb-6">
          <h2 className="text-base font-semibold text-text-primary mb-3">Backlog Trend (Active Tickets Over Time)</h2>
          <p className="text-xs text-slate-500 mb-3">Excludes Closed, Cancelled, and Resolved. Shows true active workload.</p>
          <div className="overflow-y-auto max-h-[200px]">
            <table className="w-full border-collapse text-[13px]">
              <thead className="sticky top-0 bg-white">
                <tr className="border-b-2 border-border">
                  <th className="text-left py-2 px-2 font-medium text-slate-600">Week</th>
                  <th className="text-right py-2 px-2 font-medium text-slate-600">Open Tickets</th>
                  <th className="text-left py-2 px-2 font-medium text-slate-600">Trend</th>
                </tr>
              </thead>
              <tbody>
                {data.backlogTrend.map((b) => {
                  const maxOpen = Math.max(...data.backlogTrend.map(x => x.openAtEnd), 1);
                  return (
                    <tr key={b.weekStart} className="border-b border-border-subtle">
                      <td className="py-1.5 px-2">{new Date(b.weekStart.length === 10 ? b.weekStart + 'T12:00:00' : b.weekStart).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</td>
                      <td className="py-1.5 px-2 text-right font-medium">{b.openAtEnd}</td>
                      <td className="py-1.5 px-2">
                        <div className="w-full bg-slate-100 rounded-full h-2">
                          <div className="h-2 rounded-full bg-amber-500" style={{ width: `${(b.openAtEnd / maxOpen) * 100}%` }} />
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Call-Ticket Correlation */}
      {data.callTicketCorrelation && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6 text-blue-800 text-sm">
          📞 <strong>Call-Ticket Correlation:</strong> {data.callTicketCorrelation.correlatedWithCall} of {data.callTicketCorrelation.totalTickets} tickets ({data.callTicketCorrelation.correlationPercent?.toFixed(0) ?? 0}%) were created within 10 minutes of a help desk call — likely phone-originated.
        </div>
      )}

      {/* Same-Day Close Rate */}
      {data.sameDayRate && data.sameDayRate.length > 0 && (
        <div className="bg-white rounded-lg border border-border p-5 mb-6">
          <h2 className="text-base font-semibold text-text-primary mb-3">Same-Day Resolution Rate</h2>
          <p className="text-xs text-slate-500 mb-3">% of resolved tickets closed the same day they were created.</p>
          <div className="space-y-2">
            {data.sameDayRate.map((a) => (
              <div key={a.ResponsibleFullName} className="flex items-center gap-3">
                <span className="text-sm w-40 truncate">{a.ResponsibleFullName}</span>
                <div className="flex-1 bg-slate-100 rounded-full h-3">
                  <div className="h-3 rounded-full bg-emerald-500" style={{ width: `${Math.min(a.rate, 100)}%` }} />
                </div>
                <span className="text-sm font-medium w-16 text-right">{a.rate.toFixed(0)}%</span>
                <span className="text-xs text-slate-400 w-20 text-right">{a.sameDayClosed}/{a.totalClosed}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Aging Open Tickets */}
      {data.agingTickets && data.agingTickets.length > 0 && (
        <div className="bg-white rounded-lg border border-border p-5 mb-6">
          <h2 className="text-base font-semibold text-text-primary mb-3">Oldest Open Tickets</h2>
          <p className="text-xs text-slate-500 mb-3">Stale work — tickets that have been open the longest without resolution.</p>
          <div className="overflow-y-auto max-h-[300px]">
            <table className="w-full border-collapse text-[13px]">
              <thead className="sticky top-0 bg-white">
                <tr className="border-b-2 border-border">
                  <th className="text-left py-2 px-2 font-medium text-slate-600">Analyst</th>
                  <th className="text-left py-2 px-2 font-medium text-slate-600">Ticket</th>
                  <th className="text-right py-2 px-2 font-medium text-slate-600">Days Open</th>
                  <th className="text-left py-2 px-2 font-medium text-slate-600">Status</th>
                </tr>
              </thead>
              <tbody>
                {data.agingTickets.map((t) => (
                  <tr key={t.ticketId} className="border-b border-border-subtle">
                    <td className="py-1.5 px-2">{t.ResponsibleFullName}</td>
                    <td className="py-1.5 px-2 text-slate-600">{t.title}</td>
                    <td className={`py-1.5 px-2 text-right font-medium ${t.daysOpen > 14 ? 'text-red-600' : t.daysOpen > 7 ? 'text-amber-600' : ''}`}>{t.daysOpen}</td>
                    <td className="py-1.5 px-2 text-xs">{t.statusName}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Repeat Requestors + Location Hotspots */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
        {/* Repeat Requestors */}
        {data.repeatRequestors && data.repeatRequestors.length > 0 && (
          <div className="bg-white rounded-lg border border-border p-5">
            <h2 className="text-sm font-semibold text-text-primary mb-3">Repeat Requestors (5+ tickets)</h2>
            <div className="overflow-y-auto max-h-[250px]">
              <table className="w-full border-collapse text-[12px]">
                <thead className="sticky top-0 bg-white">
                  <tr className="border-b border-border">
                    <th className="text-left py-1.5 px-2 font-medium text-slate-600">Name</th>
                    <th className="text-right py-1.5 px-2 font-medium text-slate-600">Tickets</th>
                  </tr>
                </thead>
                <tbody>
                  {data.repeatRequestors.map((r) => (
                    <tr key={r.RequestorEmail || r.RequestorName} className="border-b border-border-subtle">
                      <td className="py-1 px-2">{r.RequestorName}</td>
                      <td className="py-1 px-2 text-right font-medium">{r.ticketCount}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Location Hotspots */}
        {data.locationHotspots && data.locationHotspots.length > 0 && (
          <div className="bg-white rounded-lg border border-border p-5">
            <h2 className="text-sm font-semibold text-text-primary mb-3">Location Hotspots (tickets/employee)</h2>
            <div className="overflow-y-auto max-h-[250px]">
              <table className="w-full border-collapse text-[12px]">
                <thead className="sticky top-0 bg-white">
                  <tr className="border-b border-border">
                    <th className="text-left py-1.5 px-2 font-medium text-slate-600">Site</th>
                    <th className="text-left py-1.5 px-2 font-medium text-slate-600">Branch</th>
                    <th className="text-right py-1.5 px-2 font-medium text-slate-600">Tickets</th>
                    <th className="text-right py-1.5 px-2 font-medium text-slate-600">Employees</th>
                    <th className="text-right py-1.5 px-2 font-medium text-slate-600">Per Emp</th>
                  </tr>
                </thead>
                <tbody>
                  {data.locationHotspots.map((l) => (
                    <tr key={l.siteNumber} className="border-b border-border-subtle">
                      <td className="py-1 px-2">
                        <a href={`/sites/${l.siteNumber}`} className="text-baker-red no-underline hover:underline">{l.siteNumber}</a>
                      </td>
                      <td className="py-1 px-2">{l.branchName}</td>
                      <td className="py-1 px-2 text-right">{l.ticketCount}</td>
                      <td className="py-1 px-2 text-right">{l.employeeCount}</td>
                      <td className={`py-1 px-2 text-right font-medium ${l.ticketsPerEmployee > 3 ? 'text-red-600' : ''}`}>{l.ticketsPerEmployee?.toFixed(1)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function KPI({ label, value, sub, alert }: { label: string; value: string; sub?: string; alert?: boolean }) {
  return (
    <div className={`rounded-lg border p-4 ${alert ? 'bg-red-50 border-red-200' : 'bg-white border-border'}`}>
      <div className="text-xs font-medium text-slate-500 uppercase tracking-wide">{label}</div>
      <div className={`text-2xl font-bold mt-1 ${alert ? 'text-red-600' : 'text-text-primary'}`}>{value}</div>
      {sub && <div className="text-xs text-slate-400 mt-0.5">{sub}</div>}
    </div>
  );
}
